import { SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import CampaignCard from "./CampaignCard";
import CampaignCardSkeleton from "./CampaignCardSkeleton";

import Icons from "@/components/icons";
import EmptyState from "@/components/ui/v2/empty-state";
import { ROUTES } from "@/constants/routes";
import { getCampaignStatus } from "@/features/campaigns/utils/getCampaignStatus";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { CampaignTypeDTO } from "@/types/response/CampaignType";

interface CampaignListProps {
  campaigns: CampaignTypeDTO[];
  queryString: string;
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

function CampaignList({
  campaigns,
  queryString,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: CampaignListProps) {
  const { canCreate } = usePermissionMatrix();
  const { t } = useTranslation();
  // Show skeleton cards on initial load
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <CampaignCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={t("campaigns.noData.title", "No campaigns found")}
        description={
          canCreate("campaigns")
            ? t(
              "campaigns.noData.description",
              "Get started by creating a campaign"
            )
            : undefined
        }
        action={
          canCreate("campaigns")
            ? {
              label: t("campaigns.noData.createButton", "Create Campaign"),
              href: ROUTES.new,
              icon: <Icons.AddIcon className="size-4 mr-2" />,
            }
            : undefined
        }
      />
    );
  }
  const activeCampaigns = campaigns.filter(
    (campaign) => getCampaignStatus(campaign.integrations || []).status === "active"
  );

  const pausedCampaigns = campaigns.filter(
    (campaign) => getCampaignStatus(campaign.integrations || []).status === "paused"
  );

  const inactiveCampaigns = campaigns.filter(
    (campaign) => getCampaignStatus(campaign.integrations || []).status === "inactive"
  );

  const allCampaigns = [...activeCampaigns, ...pausedCampaigns, ...inactiveCampaigns];
  // Show skeleton at bottom when:
  // 1. Loading more pages (hasNextPage && isFetchingNextPage)
  // 2. Loading state (isLoading) - show regardless of hasNextPage
  const showBottomSkeleton =
    (hasNextPage && isFetchingNextPage) || (isLoading && campaigns.length > 0);

  return (
    <InfiniteScroll
      dataLength={allCampaigns.length}
      next={fetchNextPage || (() => {})}
      hasMore={hasNextPage || false}
      loader={
        showBottomSkeleton ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <CampaignCardSkeleton key={i} />
            ))}
          </div>
        ) : null
      }
      scrollThreshold={0.9}
      scrollableTarget="main-content-container"
    >
      <div className="space-y-8">
        {activeCampaigns.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  queryString={queryString}
                />
              ))}
            </div>
          </div>
        )}

        {pausedCampaigns.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pausedCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  queryString={queryString}
                />
              ))}
            </div>
          </div>
        )}

        {inactiveCampaigns.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {inactiveCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  queryString={queryString}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </InfiniteScroll>
  );
}

export default CampaignList;
